// Canonical URL helper. Every public page points to its clean archen.io
// address as the official version, never the internal /mockup path.
export const SITE_URL = 'https://archen.io';

export function setCanonical(path) {
  const url = SITE_URL + (path && path.startsWith('/') ? path : `/${path || ''}`);

  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);

  let og = document.head.querySelector('meta[property="og:url"]');
  if (!og) {
    og = document.createElement('meta');
    og.setAttribute('property', 'og:url');
    document.head.appendChild(og);
  }
  og.setAttribute('content', url);
}

// Sets the page-level robots directive. Used to noindex pages that are
// deliberately held back from publish (business decision, not a content bug)
// while still resetting to "index, follow" on every other route, since meta
// tags persist across client-side navigation in an SPA.
export function setRobotsMeta(content = 'index, follow') {
  let meta = document.head.querySelector('meta[name="robots"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

const MONTHS = {
  january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
  july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
};

// "August 2026" | "June 12, 2026" -> ISO date (first of month when no day given).
function toIso(publishDate) {
  if (!publishDate) return undefined;
  const dayMatch = publishDate.match(/([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/);
  if (dayMatch) {
    const mm = MONTHS[dayMatch[1].toLowerCase()];
    if (mm) return `${dayMatch[3]}-${mm}-${String(dayMatch[2]).padStart(2, '0')}`;
  }
  const monthMatch = publishDate.match(/([A-Za-z]+)\s+(\d{4})/);
  if (monthMatch) {
    const mm = MONTHS[monthMatch[1].toLowerCase()];
    if (mm) return `${monthMatch[2]}-${mm}-01`;
  }
  return undefined;
}

// Injects Article JSON-LD matching a single Insights piece. Returns a cleanup
// function that removes it, so it never leaks onto other pages.
export function setArticleJsonLd(pub) {
  if (!pub) return () => {};
  const url = `${SITE_URL}/insights/${pub.slug}`;
  const image = pub.featuredImage || pub.coverImage || pub.socialImage;
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: pub.title,
    description: pub.seo?.description || pub.summary,
    ...(image ? { image: [image] } : {}),
    author: { '@type': 'Organization', name: pub.author || 'Archen™', url: SITE_URL },
    publisher: { '@id': `${SITE_URL}/#organization` },
    ...(toIso(pub.publishDate) ? { datePublished: toIso(pub.publishDate) } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };
  let el = document.getElementById('ld-article');
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'ld-article';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => {
    document.getElementById('ld-article')?.remove();
  };
}

// Injects a BreadcrumbList. `items` is an ordered array of { name, path }
// from top level to the current page. Returns a cleanup function.
export function setBreadcrumbJsonLd(items) {
  if (!items || !items.length) return () => {};
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
  let el = document.getElementById('ld-breadcrumb');
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'ld-breadcrumb';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => {
    document.getElementById('ld-breadcrumb')?.remove();
  };
}
